// Admin clients list: view all clients, create a new client, and fast-revoke
// (disable) / re-enable a client.
//
// SECURITY: disabling a client is the fast per-client revocation invariant — it
// disables all the client's API keys and revokes their sessions immediately. The
// backend returns how many keys/sessions were affected; we surface that in the
// toast so the action is observable. Errors are surfaced from the sanitized
// ApiError.message only.
//
// CHROME: NEXOVA AdminShell + nexus primitives (DataTable, ToneBadge, Modal).
// The data hooks, API calls, and form behavior are unchanged from the legacy
// screen — only the presentation moved to the NEXOVA design system.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  Modal,
  StatusPill,
  type Column,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface Client {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  // Unix seconds (the admin backend serializes c.CreatedAt.Unix()).
  created_at: number;
}

interface ClientList {
  clients: Client[];
}

interface DisableResponse {
  client_id: string;
  enabled: false;
  keys_disabled: number;
  sessions_revoked: number;
}

const createSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Enter a valid email address"),
});

type CreateValues = z.infer<typeof createSchema>;

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // The client pending a Disable confirmation, or null when the confirm dialog
  // is closed. Disable is a destructive fast-revoke, so it's confirm-gated.
  const [confirmDisable, setConfirmDisable] = useState<Client | null>(null);

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", email: "" },
  });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ClientList>("/admin/clients");
      setClients(res.clients);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function disableClient(c: Client) {
    setBusy(true);
    try {
      const res = await api.post<DisableResponse>(
        `/admin/clients/${c.id}/disable`,
      );
      toast.success(
        `Disabled — keys: ${res.keys_disabled}, sessions: ${res.sessions_revoked}`,
      );
      setConfirmDisable(null);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function enableClient(c: Client) {
    setBusy(true);
    try {
      await api.post(`/admin/clients/${c.id}/enable`);
      toast.success("Client enabled");
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(values: CreateValues) {
    try {
      await api.post<Client>("/admin/clients", values);
      toast.success("Client created");
      setCreateOpen(false);
      form.reset();
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  const columns: Column<Client>[] = [
    {
      key: "name",
      header: "Name",
      cell: (c) => (
        <Link
          to={`/clients/${c.id}`}
          className="font-medium text-nx-text underline-offset-4 hover:underline"
        >
          {c.name}
        </Link>
      ),
    },
    {
      key: "email",
      header: "Email",
      cell: (c) => <span className="text-nx-text-2">{c.email}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (c) => (
        <StatusPill tone={c.enabled ? "credit" : "danger"}>
          {c.enabled ? "enabled" : "disabled"}
        </StatusPill>
      ),
    },
    {
      key: "created",
      header: "Created",
      cell: (c) => (
        <span className="text-sm text-nx-text-2 tabular-nums">
          {new Date(c.created_at * 1000).toLocaleString()}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (c) =>
        c.enabled ? (
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() => setConfirmDisable(c)}
          >
            Disable
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => enableClient(c)}
          >
            Enable
          </Button>
        ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Clients</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Every client tenant. Disabling a client is the fast per-client
            revocation — it drops all their API keys and sessions immediately.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={busy}>
          Create client
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load clients</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={clients}
          rowKey={(c) => c.id}
          empty="No clients yet. Create one to get started."
        />
      )}

      {/* Create client dialog. */}
      <Modal
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) form.reset();
        }}
        title="Create client"
        description="Add a new client. They will be created enabled."
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onCreate)}
            className="grid gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Create
              </Button>
            </div>
          </form>
        </Form>
      </Modal>

      {/* Disable confirmation (destructive, confirm-gated). */}
      <Modal
        open={confirmDisable !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDisable(null);
        }}
        title={`Disable ${confirmDisable?.name}?`}
        description="This immediately disables all its API keys and revokes its sessions."
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDisable(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={() => confirmDisable && disableClient(confirmDisable)}
            >
              Confirm
            </Button>
          </>
        }
      />
    </AdminShell>
  );
}
